import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, MessageCircle, Video, Sparkles } from "lucide-react";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { track, EVENT } from "@/lib/analytics";

/**
 * Certificate Purchasing & Album-Verification Experience — public
 * dedicated route at `/certificates/order`. Server owns pricing.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALBUM_OPTIONS = [
  { year: 2026, host: "United States, Canada & Mexico", covers: ["Soft Cover", "Hard Cover"] },
  { year: 2022, host: "Qatar",                covers: ["Soft Cover", "Hard Cover"] },
  { year: 2018, host: "Russia",               covers: ["Soft Cover", "Hard Cover"] },
  { year: 2014, host: "Brazil",               covers: ["Soft Cover", "Hard Cover"] },
  { year: 2010, host: "South Africa",         covers: [] },
  { year: 2006, host: "Germany",              covers: [] },
  { year: 2002, host: "South Korea & Japan",  covers: [] },
  { year: 1998, host: "France",               covers: [] },
  { year: 1994, host: "United States",        covers: [] },
  { year: 1990, host: "Italy",                covers: [] },
  { year: 1986, host: "Mexico",               covers: [] },
  { year: 1982, host: "Spain",                covers: [] },
  { year: 1978, host: "Argentina",            covers: [] },
  { year: 1974, host: "West Germany",         covers: [] },
  { year: 1970, host: "Mexico",               covers: [] },
];

const DOLLARS = (c) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function CertificateOrderPage() {
  const nav = useNavigate();
  const { collector } = useCollectorAuth();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const [c, setC] = useState({
    first_name: "", last_name: "",
    email: collector?.email || "", phone: collector?.phone || "",
  });
  const [ship, setShip] = useState({ line1: "", line2: "", city: "", state: "",
                                       postal_code: "", country: "United States" });
  const [year, setYear] = useState(2026);
  const [cover, setCover] = useState("Soft Cover");
  const [displayName, setDisplayName] = useState("");
  const [completionDate, setCompletionDate] = useState("");

  // Re-fetch canonical pricing every time quantity changes so the
  // breakdown shown to the customer matches what the server will
  // actually charge. `quantity` is clamped to [1, 20] before send.
  useEffect(() => {
    const q = Math.max(1, Math.min(20, Number(quantity) || 1));
    axios.get(`${API}/certificate-orders/pricing?quantity=${q}`)
      .then(r => setPricing(r.data.pricing))
      .catch(() => {});
  }, [quantity]);

  useEffect(() => {
    // Fire once per page-open. `year`/`cover` reflect the initial
    // form values so the funnel shows the starting selection.
    track(EVENT.CERTIFICATE_ORDER_STARTED, {
      album_year: year,
      cover_type: cover,
      source_page: "certificate-order",
    }, { once: true, key: "cert-order-start" });
  }, []);
  const chosen = useMemo(() => ALBUM_OPTIONS.find((a) => a.year === year), [year]);

  const set = (k) => (e) => setC((prev) => ({ ...prev, [k]: e.target.value }));
  const sset = (k) => (e) => setShip((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async () => {
    if (!c.first_name.trim() || !c.last_name.trim()) return toast.error("First and last name required.");
    if (!c.email.trim()) return toast.error("Email required.");
    if (!c.phone.trim()) return toast.error("Phone required.");
    for (const k of ["line1","city","state","postal_code","country"]) {
      if (!ship[k].trim()) return toast.error(`Shipping ${k.replace("_"," ")} required.`);
    }
    if (!displayName.trim()) return toast.error("Please enter the name to display on the certificate.");
    if (!completionDate) return toast.error("Please select the album completion date.");
    setBusy(true);
    try {
      const albumTitle = chosen ? (chosen.covers.length && cover ? `${year} ${chosen.host} — ${cover}` : `${year} ${chosen.host}`) : `${year}`;
      const r = await axios.post(`${API}/certificate-orders`, {
        customer: c,
        shipping_address: ship,
        album: {
          year, host_country: chosen?.host || "",
          cover_type: chosen?.covers?.length ? cover : null,
          album_title: albumTitle,
          collector_display_name: displayName.trim(),
          completion_date: completionDate,
        },
        quantity: Math.max(1, Math.min(20, Number(quantity) || 1)),
      }, { withCredentials: true });
      const { public_order_number, status_token } = r.data;
      // Anonymous conversion. Certificate number / claim code stay
      // local — never transmitted (would be blocked by the PII
      // scrubber anyway).
      track(EVENT.CERTIFICATE_ORDER_SUBMITTED, {
        album_year:      year,
        cover_type:      chosen?.covers?.length ? cover : "default",
        order_value:     Number(r.data?.pricing?.total_cents ? r.data.pricing.total_cents / 100 : 10.7),
        currency:        "USD",
        delivery_method: r.data?.delivery?.code || "usps_stamp",
      }, { once: true, key: public_order_number });
      const dest = status_token
        ? `/certificates/order/${public_order_number}?t=${status_token}`
        : `/certificates/order/${public_order_number}`;
      nav(dest);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submit failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="cert-order-page">
      {/* Hero */}
      <section className="bg-[var(--ink)] text-white">
        <div className="container-gm py-10 md:py-14">
          <span className="sticker sticker-yellow mb-4 inline-block">Album Complete Certificate</span>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight" data-testid="cert-order-hero-title">
            Complete Album? <span className="text-[var(--collector-gold)]">Make it official.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/85">
            Get a numbered physical Album Complete Certificate and a digital
            certificate recorded on CollectorUp <strong>after your album is verified</strong>.
          </p>
        </div>
      </section>

      <div className="container-gm py-10 md:py-14 grid gap-10 lg:grid-cols-[1fr_360px]">
        <div className="space-y-8">
          {/* How it works */}
          <section data-testid="cert-order-how-it-works">
            <h2 className="font-serif text-3xl mb-4">How it works</h2>
            <ol className="space-y-4 text-[15px] text-[var(--ink)]/90">
              <Step n={1} title="Place your order">Complete the checkout and follow the displayed payment instructions.</Step>
              <Step n={2} title="Send your verification video">After ordering, tap the WhatsApp button on the confirmation page. Send the prefilled message first, then attach your video.</Step>
              <Step n={3} title="Show that your album is complete">
                <ul className="mt-2 list-disc list-inside space-y-1 text-sm" data-testid="cert-video-rules">
                  <li><strong>180 seconds or less</strong></li>
                  <li>State your <strong>first and last name</strong></li>
                  <li>State the <strong>current date</strong></li>
                  <li>Show the <strong>front of the album</strong></li>
                  <li>Show <strong>every album page clearly</strong></li>
                  <li>Demonstrate the album is complete</li>
                </ul>
              </Step>
              <Step n={4} title="Review and approval">Gil will review the video. If it&apos;s unclear you may be asked for a new one. <strong>Payment does not guarantee approval.</strong> The certificate will not be prepared or shipped before approval.</Step>
            </ol>
          </section>

          {/* Album */}
          <section className="rounded-3xl border-2 border-[var(--ink)] bg-white p-5 md:p-6">
            <h2 className="font-serif text-2xl mb-4">Album details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="kicker kicker-black text-[11px] mb-1">World Cup year</div>
                <select data-testid="cert-order-album-year" value={year} onChange={(e) => setYear(parseInt(e.target.value,10))} className="gm-field w-full">
                  {ALBUM_OPTIONS.map(a => <option key={a.year} value={a.year}>{a.year} · {a.host}</option>)}
                </select>
              </label>
              {chosen?.covers?.length ? (
                <label className="block">
                  <div className="kicker kicker-black text-[11px] mb-1">Cover type</div>
                  <select data-testid="cert-order-cover" value={cover} onChange={(e) => setCover(e.target.value)} className="gm-field w-full">
                    {chosen.covers.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
              ) : <div />}
              <label className="block md:col-span-2">
                <div className="kicker kicker-black text-[11px] mb-1">Certificate display name</div>
                <input data-testid="cert-order-display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="gm-field w-full" placeholder="Name exactly as it should appear on the certificate" />
              </label>
              <label className="block">
                <div className="kicker kicker-black text-[11px] mb-1">Album completion date</div>
                <input type="date" data-testid="cert-order-completion-date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} className="gm-field w-full" />
              </label>
            </div>
          </section>

          {/* Customer + shipping */}
          <section className="rounded-3xl border-2 border-[var(--ink)] bg-white p-5 md:p-6">
            <h2 className="font-serif text-2xl mb-4">Your details</h2>
            {!collector && (
              <p className="text-xs text-[var(--smoke)] mb-3">
                To protect your order and receive updates, <Link className="underline" to="/login">sign in</Link> or enter your contact information as a guest.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="First name *" testid="cert-first" value={c.first_name} onChange={set("first_name")} />
              <Input label="Last name *"  testid="cert-last"  value={c.last_name}  onChange={set("last_name")} />
              <Input label="Email *"      testid="cert-email" value={c.email}      onChange={set("email")} type="email" />
              <Input label="Phone *"      testid="cert-phone" value={c.phone}      onChange={set("phone")} />
            </div>
            <h3 className="mt-6 kicker kicker-black text-[11px]">Shipping address (required)</h3>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Street *"    testid="cert-ship-line1"      value={ship.line1}       onChange={sset("line1")} />
              <Input label="Apt / Suite" testid="cert-ship-line2"      value={ship.line2}       onChange={sset("line2")} />
              <Input label="City *"      testid="cert-ship-city"       value={ship.city}        onChange={sset("city")} />
              <Input label="State *"     testid="cert-ship-state"      value={ship.state}       onChange={sset("state")} />
              <Input label="ZIP *"       testid="cert-ship-postal"     value={ship.postal_code} onChange={sset("postal_code")} />
              <Input label="Country *"   testid="cert-ship-country"    value={ship.country}     onChange={sset("country")} />
            </div>
          </section>
        </div>

        {/* Sticky sidebar */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--panini-yellow)] p-5">
            <h3 className="font-serif text-2xl mb-3">Order summary</h3>

            {/* Quantity selector */}
            <div className="mb-4" data-testid="cert-order-quantity-block">
              <div className="kicker kicker-black text-[11px] mb-2">Certificate quantity</div>
              <div className="inline-flex items-center gap-3 border-2 border-[var(--ink)] rounded-xl px-2 py-1">
                <button
                  type="button"
                  data-testid="cert-qty-dec"
                  aria-label="Decrease quantity"
                  onClick={() => setQuantity(q => Math.max(1, (Number(q) || 1) - 1))}
                  disabled={quantity <= 1}
                  className="w-9 h-9 grid place-items-center rounded-lg bg-[var(--ink)]/5 hover:bg-[var(--ink)]/10 disabled:opacity-40 disabled:cursor-not-allowed text-xl font-bold"
                >−</button>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={quantity}
                  data-testid="cert-qty-input"
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (Number.isNaN(v)) { setQuantity(1); return; }
                    setQuantity(Math.max(1, Math.min(20, v)));
                  }}
                  className="w-14 text-center font-bold text-lg bg-transparent outline-none"
                  aria-label="Certificate quantity"
                />
                <button
                  type="button"
                  data-testid="cert-qty-inc"
                  aria-label="Increase quantity"
                  onClick={() => setQuantity(q => Math.min(20, (Number(q) || 1) + 1))}
                  disabled={quantity >= 20}
                  className="w-9 h-9 grid place-items-center rounded-lg bg-[var(--ink)]/5 hover:bg-[var(--ink)]/10 disabled:opacity-40 disabled:cursor-not-allowed text-xl font-bold"
                >+</button>
              </div>
              <p className="text-[11px] text-[var(--smoke)] mt-1">Max 20 certificates per order.</p>
            </div>

            {/* Server-authoritative breakdown */}
            <div className="text-sm space-y-1.5" data-testid="cert-order-breakdown">
              <Line
                label={`Certificates (${pricing?.quantity ?? quantity} × $${((pricing?.unit_price_cents ?? 500) / 100).toFixed(2)})`}
                cents={pricing?.certificate_subtotal_cents ?? ((pricing?.unit_price_cents ?? 500) * (pricing?.quantity ?? quantity))}
                data-testid="cert-order-cert-subtotal"
              />
              <Line
                label="Sales Tax (7%)"
                cents={pricing?.tax_cents ?? 0}
                data-testid="cert-order-tax"
              />
              <Line
                label="Shipping"
                cents={pricing?.shipping_cents ?? 500}
                data-testid="cert-order-shipping"
              />
              <div className="border-t border-[var(--ink)]/10 my-2" />
              <Line
                label="Total"
                cents={pricing?.total_cents ?? 1035}
                bold big
                data-testid="cert-order-total"
              />
            </div>
            <button
              data-testid="cert-order-submit"
              onClick={submit}
              disabled={busy}
              className="mt-5 w-full btn-red inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Placing order…" : "Place order"} <ArrowRight size={18} />
            </button>
            <p className="text-[11px] text-[var(--smoke)] mt-3">
              Your certificate will not be prepared or shipped until your
              album-verification video has been reviewed and approved.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  const icons = {
    1: <ShieldCheck key="ic1" size={18}/>,
    2: <MessageCircle key="ic2" size={18}/>,
    3: <Video key="ic3" size={18}/>,
    4: <Sparkles key="ic4" size={18}/>,
  };
  return (
    <li className="flex gap-3">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--panini-red)] text-white text-sm font-bold shrink-0">{n}</span>
      <div>
        <div className="font-bold flex items-center gap-2">{title} {icons[n]}</div>
        <div className="text-sm text-[var(--ink)]/80">{children}</div>
      </div>
    </li>
  );
}

function Input({ label, testid, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <div className="kicker kicker-black text-[11px] mb-1">{label}</div>
      <input data-testid={testid} value={value} onChange={onChange} type={type} className="gm-field w-full" />
    </label>
  );
}

function Line({ label, cents, bold, big, "data-testid": testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex items-center justify-between ${bold ? "font-bold" : ""} ${big ? "text-lg" : ""}`}
    >
      <span>{label}</span>
      <span>{DOLLARS(cents)}</span>
    </div>
  );
}
